import { useCallback } from 'react';

import { graphql } from 'react-relay';

import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { duplicateMauiApp as duplicateMauiAppApi } from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions/duplicate-maui-app';

import type {
	useMauiMoveMutation$data,
	useMauiMoveMutation$variables,
} from './__generated__/useMauiMoveMutation.graphql.ts';
import type {
	useMauiMoveTopLevelMutation$data,
	useMauiMoveTopLevelMutation$variables,
} from './__generated__/useMauiMoveTopLevelMutation.graphql.ts';

/**
 * Hook that provides a function to move a MAUI diagram whiteboard to be a child
 * of a target page.
 *
 * Uses a two-step move to handle cross-space moves (whiteboard starts in a Rovo
 * system space where the user is not an admin):
 * 1. `movePageTopLevel` — moves the whiteboard to the top level of the target space
 * 2. `movePageAppend` — reparents the whiteboard under the target page
 *
 * This is the same approach the Confluence Move Page Dialog uses for cross-space
 * moves (see useMovePageHandler in page-tree).
 */
export function useMauiMove(
	targetSpaceKey: string | undefined,
	cloudId?: string,
	contentType?: string,
) {
	const [commitMoveTopLevel] = useMutationInterop<
		useMauiMoveTopLevelMutation$data,
		useMauiMoveTopLevelMutation$variables
	>(graphql`
		mutation useMauiMoveTopLevelMutation($pageId: ID!, $spaceKey: String!) {
			movePageTopLevel(input: { pageId: $pageId, targetSpaceKey: $spaceKey }) {
				page {
					id
					links {
						webui
						editui
					}
				}
			}
		}
	`);

	const [commitMoveAppend] = useMutationInterop<
		useMauiMoveMutation$data,
		useMauiMoveMutation$variables
	>(graphql`
		mutation useMauiMoveMutation($pageId: ID!, $parentId: ID!) {
			movePageAppend(input: { pageId: $pageId, parentId: $parentId }) {
				page {
					id
					links {
						webui
						editui
					}
				}
			}
		}
	`);

	const moveWhiteboardToPage = useCallback(
		async (whiteboardContentId: string, targetParentPageId: string): Promise<void> => {
			if (!targetSpaceKey) {
				throw new Error('Target space key is not available.');
			}

			// Step 1: Move whiteboard to the top level of the target space.
			// This avoids the "only space admins can move content to another space"
			// permission error that movePageAppend alone would throw for cross-space moves.
			const topLevelResult = await commitMoveTopLevel({
				variables: {
					pageId: whiteboardContentId,
					spaceKey: targetSpaceKey,
				},
			});

			if (!topLevelResult.data?.movePageTopLevel?.page) {
				throw new Error(
					"We couldn't move this diagram to your page. It won't be visible to other users until it's moved out of its temporary space.",
				);
			}

			// Step 2: Reparent whiteboard under the target page (now within the same space).
			const appendResult = await commitMoveAppend({
				variables: {
					pageId: whiteboardContentId,
					parentId: targetParentPageId,
				},
			});

			if (!appendResult.data?.movePageAppend?.page) {
				throw new Error(
					"We couldn't move this diagram to your page. It won't be visible to other users until it's moved out of its temporary space.",
				);
			}
		},
		[commitMoveAppend, commitMoveTopLevel, targetSpaceKey],
	);

	const duplicateMauiApp = useCallback(
		(
			appId: string,
			containerContentId: string,
			overrideContentType?: string,
		): Promise<{ id: string }> => {
			return duplicateMauiAppApi(appId, {
				containerContentId,
				cloudId,
				contentType: overrideContentType ?? contentType,
			});
		},
		[cloudId, contentType],
	);

	return {
		moveWhiteboardToPage,
		duplicateMauiApp,
	};
}
